import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fmtEUR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Minus, Plus, ChevronRight, Star } from "lucide-react";
import { useT } from "@/context/I18nContext";

function JsonLd({ product, avg, reviewCount }) {
  const ld = {
    "@context": "https://schema.org", "@type": "Product",
    name: product.name,
    description: product.long_description || product.description,
    image: product.images || [],
    brand: { "@type": "Brand", name: "Brunchi" },
    offers: {
      "@type": "Offer", url: `${window.location.origin}/shop/${product.slug}`,
      priceCurrency: "EUR", price: product.price_eur,
      availability: product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    },
    ...(reviewCount > 0 ? {
      aggregateRating: { "@type": "AggregateRating", ratingValue: avg.toFixed(1), reviewCount },
    } : {}),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />;
}

export default function ProductDetail() {
  const { slug } = useParams();
  const { t } = useT();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const { add } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      if (r.data.slug && r.data.slug !== slug) { nav(`/shop/${r.data.slug}`, { replace: true }); return; }
      setP(r.data);
    }).catch(() => setP(false));
    api.get(`/products/${slug}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [slug]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.info(t("pdp.reviews.sign_in")); nav("/auth"); return; }
    if (!reviewForm.comment.trim()) return;
    setSubmittingReview(true);
    try {
      await api.post(`/products/${slug}/reviews`, reviewForm);
      toast.success(t("pdp.reviews.submitted"));
      setReviewForm({ rating: 5, title: "", comment: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("pdp.reviews.submit_failed"));
    } finally { setSubmittingReview(false); }
  };

  const avg = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length) : 0;

  if (p === null) return <div className="max-w-7xl mx-auto px-6 py-24 text-center text-[color:var(--brunchi-ink-soft)]">{t("pdp.loading")}</div>;
  if (p === false) return (
    <div className="max-w-7xl mx-auto px-6 py-24 text-center">
      <div className="font-serif text-2xl mb-2">{t("pdp.not_found")}</div>
      <button onClick={() => nav("/shop")} className="mt-4 text-[color:var(--brunchi-terracotta)] underline">{t("pdp.back")}</button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-14" data-testid="product-detail">
      <JsonLd product={p} avg={avg} reviewCount={reviews.length} />
      <div className="text-xs text-[color:var(--brunchi-ink-soft)] mb-6 flex items-center gap-1">
        <button onClick={() => nav("/shop")} className="hover:text-[color:var(--brunchi-terracotta)]">{t("nav.shop")}</button>
        <ChevronRight size={12} />
        <span>{p.name}</span>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div className="aspect-square rounded-2xl overflow-hidden bg-[color:var(--brunchi-cream-2)]">
          {p.images?.[0] ? (
            <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[color:var(--brunchi-stone-2)] font-serif text-6xl">Brunchi</div>
          )}
        </div>
        <div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(p.tags || []).map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[color:var(--brunchi-cream-2)] text-[color:var(--brunchi-forest)]">{t}</span>
            ))}
          </div>
          <h1 className="font-display text-3xl lg:text-5xl leading-tight">{p.name}</h1>
          <div className="mt-2 text-sm text-[color:var(--brunchi-ink-soft)]">
            {p.weight_g ? `${p.weight_g}g · ` : ""}{t("pdp.handcrafted")}
          </div>
          <div className="mt-6 font-mono text-3xl font-semibold text-[color:var(--brunchi-terracotta)]" data-testid="product-price">
            {fmtEUR(p.price_eur)}
          </div>
          {p.box_of_12_eur && !p.is_box_variant && (
            <button
              onClick={async () => {
                try {
                  const { data } = await api.get(`/products/${p.slug}-box-of-12`);
                  add(data, 1);
                  toast.success(t("shop.box_added", { name: p.name }));
                } catch { toast.error("Box variant unavailable"); }
              }}
              data-testid="pdp-add-box"
              className="mt-3 inline-flex items-center gap-2 px-5 py-2 rounded-full pill-outline text-sm font-medium">
              {t("shop.box_of_12", { price: fmtEUR(p.box_of_12_eur) })}
            </button>
          )}
          <p className="mt-6 text-[color:var(--brunchi-ink-soft)] leading-relaxed">
            {p.long_description || p.description}
          </p>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center gap-3 border border-[color:var(--brunchi-stone-2)] rounded-full px-2 py-1.5">
              <button onClick={() => setQty(Math.max(1, qty - 1))}
                data-testid="pdp-qty-decrease" className="w-8 h-8 rounded-full hover:bg-[color:var(--brunchi-cream-2)] flex items-center justify-center">
                <Minus size={14} />
              </button>
              <span className="font-mono w-6 text-center" data-testid="pdp-qty">{qty}</span>
              <button onClick={() => setQty(qty + 1)}
                data-testid="pdp-qty-increase" className="w-8 h-8 rounded-full hover:bg-[color:var(--brunchi-cream-2)] flex items-center justify-center">
                <Plus size={14} />
              </button>
            </div>
            <button onClick={() => add(p, qty)}
              data-testid="pdp-add-to-cart"
              disabled={p.stock <= 0}
              className="flex-1 py-3.5 rounded-full pill-primary font-medium disabled:opacity-50">
              {p.stock <= 0 ? t("pdp.out_of_stock") : t("pdp.add_to_basket")}
            </button>
          </div>

          {p.ingredients?.length > 0 && (
            <div className="mt-10 border-t border-[color:var(--brunchi-stone)] pt-6">
              <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brunchi-forest)] mb-3">{t("pdp.ingredients")}</div>
              <p className="text-sm text-[color:var(--brunchi-ink-soft)]">{p.ingredients.join(", ")}</p>
            </div>
          )}

          {p.nutrition && Object.keys(p.nutrition).length > 0 && (
            <div className="mt-8 border-t border-[color:var(--brunchi-stone)] pt-6" data-testid="nutrition-panel">
              <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brunchi-forest)] mb-3">{t("pdp.nutrition")}</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {(() => {
                  const LABELS = {
                    kcal:      { label: t("nutri.kcal"),      unit: "kcal" },
                    protein_g: { label: t("nutri.protein"),   unit: "g"    },
                    carbs_g:   { label: t("nutri.carbs"),     unit: "g"    },
                    sugars_g:  { label: t("nutri.sugars"),    unit: "g"    },
                    fat_g:     { label: t("nutri.fat"),       unit: "g"    },
                    sat_fat_g: { label: t("nutri.sat_fat"),   unit: "g"    },
                    fiber_g:   { label: t("nutri.fiber"),     unit: "g"    },
                    salt_g:    { label: t("nutri.salt"),      unit: "g"    },
                  };
                  const ORDER = ["kcal","protein_g","carbs_g","sugars_g","fat_g","sat_fat_g","fiber_g","salt_g"];
                  const keys = ORDER.filter((k) => k in p.nutrition).concat(Object.keys(p.nutrition).filter((k) => !ORDER.includes(k)));
                  return keys.map((k) => {
                    const meta = LABELS[k] || { label: k.replace(/_/g, " "), unit: "" };
                    const v = p.nutrition[k];
                    return (
                      <div key={k} data-testid={`nutrition-${k}`} className="border border-[color:var(--brunchi-stone)] rounded-lg p-3">
                        <div className="text-[10px] uppercase tracking-widest text-[color:var(--brunchi-ink-soft)]">{meta.label}</div>
                        <div className="font-mono font-semibold text-lg text-[color:var(--brunchi-ink)]">
                          {v}{meta.unit ? <span className="text-xs ml-1 font-normal text-[color:var(--brunchi-ink-soft)]">{meta.unit}</span> : null}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
              <p className="mt-3 text-[10px] text-[color:var(--brunchi-ink-soft)]">{t("nutri.per_serving")}</p>
            </div>
          )}
        </div>
      </div>

      <section className="mt-16 border-t border-[color:var(--brunchi-stone)] pt-10" data-testid="reviews-section">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brunchi-green-hover)] mb-2">{t("pdp.reviews.eyebrow")}</div>
            <h2 className="font-display text-2xl">
              {reviews.length > 0 ? (
                <>
                  <span className="font-mono">{avg.toFixed(1)}</span>
                  <span className="text-[color:var(--brunchi-amber)] mx-2">★</span>
                  <span className="text-base font-normal text-[color:var(--brunchi-ink-soft)]">
                    {t("pdp.reviews.avg_from", { count: reviews.length, s: reviews.length !== 1 ? "s" : "" })}
                  </span>
                </>
              ) : t("pdp.reviews.be_first")}
            </h2>
          </div>
        </div>
        <div className="space-y-4 mb-10">
          {reviews.map((r) => (
            <div key={r.id} className="bg-white rounded-xl border border-[color:var(--brunchi-stone)] p-5" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="flex text-[color:var(--brunchi-amber)] text-sm">
                  {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}
                </div>
                <div className="font-display font-semibold">{r.user_name}</div>
                <div className="text-xs text-[color:var(--brunchi-ink-soft)] ml-auto">{r.created_at?.slice(0, 10)}</div>
              </div>
              {r.title && <div className="font-display text-base font-medium mb-1">{r.title}</div>}
              <div className="text-sm text-[color:var(--brunchi-ink-soft)]">{r.comment}</div>
            </div>
          ))}
        </div>
        <form onSubmit={submitReview} className="bg-[color:var(--brunchi-cream)] rounded-2xl p-6 border border-[color:var(--brunchi-stone)]" data-testid="review-form">
          <div className="font-display text-lg mb-4">{t("pdp.reviews.leave")}</div>
          <div className="flex items-center gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setReviewForm({ ...reviewForm, rating: n })}
                data-testid={`review-star-${n}`} className="p-1">
                <Star size={22} fill={n <= reviewForm.rating ? "#E5A24A" : "none"} stroke="#E5A24A" />
              </button>
            ))}
          </div>
          <input placeholder={t("pdp.reviews.headline")} value={reviewForm.title}
            onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
            className="w-full px-4 py-2.5 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm mb-2"
            data-testid="review-title" />
          <textarea required placeholder={t("pdp.reviews.how_was")} value={reviewForm.comment}
            onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
            rows={3}
            className="w-full px-4 py-2.5 rounded-lg border border-[color:var(--brunchi-stone-2)] bg-white text-sm mb-3"
            data-testid="review-comment" />
          <button type="submit" disabled={submittingReview}
            data-testid="review-submit-btn"
            className="px-6 py-2.5 rounded-full pill-primary font-medium disabled:opacity-40">
            {submittingReview ? t("pdp.reviews.submitting") : t("pdp.reviews.submit")}
          </button>
          <p className="text-xs text-[color:var(--brunchi-ink-soft)] mt-2">
            {t("pdp.reviews.approval_note")}
          </p>
        </form>
      </section>
    </div>
  );
}
